import userManager, { SIGNIN_OIDC_PATH } from "./OIDCAuthHandler";
import { clearUserData, getToken, setAuthError, updateUserToken } from "../../utils/persist";

export const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

let isRefreshing = false;
let refreshSubscribers = [];
let lastRefreshError = null;

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      addRefreshSubscriber((token) => resolve(token));
    });
  }

  isRefreshing = true;

  try {
    const user = await userManager.signinSilent();
    const token = (user && user.access_token) || null;
    if (token) {
      lastRefreshError = null;
      updateUserToken(token);
    }
    onTokenRefreshed(token);
    return token;
  } catch (error) {
    console.error("Silent token refresh failed:", error);
    lastRefreshError = (error && error.message) || String(error);
    onTokenRefreshed(null);
    return null;
  } finally {
    isRefreshing = false;
  }
};

// Ends the local session and returns to the sign-in screen, which displays the reason
// instead of silently bouncing the user to the IdP. clearUserData wipes the whole session
// storage, so it must run before setAuthError or the message it just wrote would be lost.
export const handleTokenRefreshFailure = (reason) => {
  const message =
    reason || (lastRefreshError ? `${SESSION_EXPIRED_MESSAGE} (${lastRefreshError})` : SESSION_EXPIRED_MESSAGE);
  lastRefreshError = null;
  console.error("Authentication failed:", message);
  clearUserData();
  setAuthError(message);
  userManager.removeUser().catch(() => {});
  window.location.replace(SIGNIN_OIDC_PATH);
};

export const initTokenRefreshListeners = () => {
  userManager.events.addAccessTokenExpired(async () => {
    if (!getToken()) return;
    if (await refreshToken()) return;
    // A single failure here can be a transient blip (e.g. the IdP request dropped), so retry once
    // before treating it as definitive - mirroring the retry-once-on-401 pattern in utils/request.js.
    if (await refreshToken()) return;
    handleTokenRefreshFailure();
  });

  // Background renewal failures are often transient (network hiccups, a dropped iframe request) and
  // oidc-client-ts keeps retrying these on its own schedule, so we only log them here. Ending the
  // session on every such error could also race with a renewal that succeeds moments later - the
  // real source of truth for a new token is addUserLoaded below, not this error event.
  userManager.events.addSilentRenewError((error) => {
    console.error("Silent renew error:", error);
  });

  userManager.events.addUserLoaded((user) => {
    if (user && user.access_token && getToken()) {
      updateUserToken(user.access_token);
    }
  });
};
